import { filter, head, pipe, take, toArray } from '@fxts/core';
import { dataStr } from '../../../../shared/util/dataStr';
import { type Html, html, View } from 'rune-ts';
import { CreatorTypeS, CreatorUtilS } from '../../../../features/Creator/outbound/share';
import { makeUtilScreenNavigate } from '../../../../shared/app/navigate';
import { getResizedImage100WebpUrl } from '../../../../shared/util/getResizedImageUrl';
import { makeDefaultProfileImg } from '../../../../shared/util/helper';
import { MShopDefaultIcon } from '../../atoms/Icon';
import klass from './CreatorRankingTopTen.module.scss';
import { font, staticFont } from '../../../../shared/typography';
import { getResizedUrl } from '../../../../shared/util/image';
import { differenceInMinutes, format, isThisMonth, isThisWeek } from 'date-fns';
import { RankDiff } from '@services/MarppleShop/renderApp/components/atoms/RankDiff/RankDiff.ts';
import { formatMonth, formatMonthWeek } from '@services/MarppleShop/shared/util/date.ts';

export type CreatorRankingTopTenProps = {
  creator_rankings: CreatorTypeS.ICreatorRanking[];
  updated_at: Date;
  ranking_page_type: CreatorTypeS.RankingPageType;
  is_mobile: boolean;
};

const default_img = makeDefaultProfileImg(300);

class CreatorTopRankerCard extends View<{
  creator_ranking: CreatorTypeS.ICreatorRanking;
  updated_at: CreatorRankingTopTenProps['updated_at'];
  ranking_page_type: CreatorRankingTopTenProps['ranking_page_type'];
  is_mobile: CreatorRankingTopTenProps['is_mobile'];
}> {
  static MO_THUMBNAIL_SIZE = 160;
  static PC_THUMBNAIL_SIZE = 448;

  protected override template() {
    return this.isMobile ? this._makeMoHtml() : this._makePcHtml();
  }

  private _makePcHtml() {
    const { name, seller_type_name } = this.data.creator_ranking;
    return html`
      <a
        class="${klass.creator_top_ranker_card}"
        style="background-image:url(${this.thumbnailImage})"
        href="${this.store_url}"
      >
        <div class="${klass.content}">
          <!--  랭킹  -->
          <div class="${klass.rank_wrapper} ${staticFont('bebas_60_bold', 'bebas_40_bold')}">
            <span class="${klass.rank_num}">TOP 1</span>
            ${new RankDiff({
              ranking: this.data.creator_ranking,
              font: staticFont('unica_24_bold'),
              klass: klass.rank_wrapper,
            })}
          </div>
          <!--  크리에이터 명  -->
          <div class="${klass.creator_name} ${font('32_bold', '16_bold')}">
            <span>${name}</span>
            ${MShopDefaultIcon()}
          </div>
          <!--  크리에이터 타입    -->
          <div class="${klass.creator_type} ${font('16_medium')}">${seller_type_name || ''}</div>
          <!--  업데이트 일자 -->
          ${this._makeUpdateTimeHtml()}
        </div>
      </a>
    `;
  }

  private _makeMoHtml() {
    const { name, seller_type_name } = this.data.creator_ranking;
    return html`
      <div class="${klass.creator_top_ranker_card}">
        <div class="${klass.top}">
          <!--  타이틀  -->
          ${this._makeTitleHtml()}

          <!--  업데이트 일자 -->
          ${this._makeUpdateTimeHtml()}
        </div>
        <div class="${klass.bottom}">
          <!--  랭킹  -->
          <div class="${klass.rank_wrapper} ${staticFont('bebas_60_bold', 'bebas_40_bold')}">
            <span class="${klass.rank_num}">TOP 1</span>
            ${new RankDiff({
              ranking: this.data.creator_ranking,
              font: staticFont('unica_16_bold'),
            })}
          </div>

          <!--  크리에이터 명  -->
          <a
            class="${klass.creator_name} ${font('32_bold', '20_bold')}"
            href="${this.store_url}"
            data-post-message="${dataStr(makeUtilScreenNavigate(this.store_url))}"
          >
            ${html.preventEscape(name || '')}
          </a>

          <!--  크리에이터 타입    -->
          <div class="${klass.creator_type} ${font('12_medium')}">${seller_type_name || ''}</div>

          <a
            class="${klass.profile_image}"
            href="${this.store_url}"
            data-post-message="${dataStr(makeUtilScreenNavigate(this.store_url))}"
          >
            <img src="${this.thumbnailImage}" alt="" />
          </a>
        </div>
      </div>
    `;
  }

  private _makeTitleHtml() {
    return html`<div class="${klass.title} ${staticFont('bebas_60_bold', 'bebas_48_bold')}">
      ${this.rankingPageTitle}<br />
      HOT CREATOR
    </div> `;
  }

  private _makeUpdateTimeHtml() {
    const date = new Date(this.data.updated_at);

    if (this.data.ranking_page_type === 'weekly' && !isThisWeek(date)) {
      return html`
        <div class="${klass.update_time}">
          <div class="${klass.minute} ${font('14_medium', '12_medium')}">${formatMonthWeek(date)}</div>
        </div>
      `;
    } else if (this.data.ranking_page_type === 'monthly' && !isThisMonth(date)) {
      return html`
        <div class="${klass.update_time}">
          <div class="${klass.minute} ${font('14_medium', '12_medium')}">${formatMonth(date)}</div>
        </div>
      `;
    } else {
      const update_time = format(date, 'yyyy.MM.dd HH:mm');
      const minutes_diff = Math.abs(differenceInMinutes(new Date(this.data.updated_at), new Date()));

      return html`
        <div class="${klass.update_time}">
          <div class="${klass.minute} ${font('14_medium', '12_medium')}">
            ${ET('mps2::main::creator_ranking::updated_at', { minute: minutes_diff })}
          </div>
          <div class="${klass.time} ${font('14_regular', '12_medium')}">${update_time}</div>
        </div>
      `;
    }
  }

  get store_url() {
    return `/${ET.lang}/${this.data.creator_ranking.domain_name}`;
  }

  get rankingPageTitle() {
    if (this.data.ranking_page_type === 'weekly') return 'WEEKLY';
    if (this.data.ranking_page_type === 'monthly') return 'MONTHLY';
    return 'REALTIME';
  }

  get thumbnailImage() {
    const { thumbnail_img } = this.data.creator_ranking;

    return getResizedUrl({
      url: thumbnail_img || default_img,
      width: this.isMobile
        ? CreatorTopRankerCard.MO_THUMBNAIL_SIZE * 2
        : CreatorTopRankerCard.PC_THUMBNAIL_SIZE * 2,
      format: 'webp',
      quality: 85,
      bg: '#FFFFFF',
    });
  }

  get isMobile(): boolean {
    return this.data.is_mobile;
  }
}

class CreatorRankerCard extends View<{
  creator_ranking: CreatorTypeS.ICreatorRanking;
  updated_at: CreatorRankingTopTenProps['updated_at'];
  ranking_page_type: CreatorRankingTopTenProps['ranking_page_type'];
  is_mobile: CreatorRankingTopTenProps['is_mobile'];
}> {
  protected override template() {
    return this.isMobile ? this._makeMoHtml() : this._makePcHtml();
  }

  private _makePcHtml() {
    const { current_rank, domain_name, seller_type_name, name } = this.data.creator_ranking;

    return html`
      <a
        data-post-message="${dataStr(makeUtilScreenNavigate(`/${ET.lang}/${domain_name}`))}"
        href="/${ET.lang}/${domain_name}"
        class="${klass.creator_ranker_card}"
      >
        <div class="${klass.rank}">
          <span class="${klass.rank_num} ${staticFont('bebas_32_bold')}">${current_rank}</span>
          ${new RankDiff({
            ranking: this.data.creator_ranking,
            rank_diff_states: this.rankDiffStates,
            klass: klass.rank_diff,
          })}
        </div>

        <!--   썸네일 이미지   -->
        ${this._makeThumbnailImage()}

        <!--   크리에이터 메타 정보     -->
        <div class="${klass.name}">
          <span class="${font('16_bold')}"> ${html.preventEscape(name ?? '')} </span>
          <span class="${font('14_medium')}"> ${seller_type_name || ''} </span>
        </div>
      </a>
    `;
  }

  private _makeMoHtml() {
    const { current_rank, domain_name, name } = this.data.creator_ranking;

    return html`
      <a
        data-post-message="${dataStr(makeUtilScreenNavigate(`/${ET.lang}/${domain_name}`))}"
        href="/${ET.lang}/${domain_name}"
        class="${klass.creator_ranker_card}"
      >
        <div class="${klass.rank}">
          <div class="${klass.rank_num} ${staticFont('bebas_24_bold')}">${current_rank}</div>
          ${new RankDiff({
            ranking: this.data.creator_ranking,
            rank_diff_states: this.rankDiffStates,
            klass: klass.rank_diff,
          })}
        </div>

        <!--   크리에이터 메타 정보     -->
        <div class="${klass.name}">
          <span class="${font('14_bold')}"> ${html.preventEscape(name ?? '')} </span>
        </div>

        <!--   썸네일 이미지   -->
        ${this._makeThumbnailImage()}
      </a>
    `;
  }

  private _makeThumbnailImage() {
    const { thumbnail_img } = this.data.creator_ranking;

    return html`
      <img
        class="${klass.thumbnail}"
        src="${thumbnail_img ? getResizedImage100WebpUrl(thumbnail_img, 300) : default_img}"
        alt="thumbnail_img"
      />
    `;
  }

  get rankDiffStates(): {
    state: CreatorUtilS.DiffIconKey;
    makeHtml: (state: CreatorUtilS.DiffIconKey, diff: number) => Html;
  }[] {
    return [
      {
        state: 'none',
        makeHtml() {
          return html``;
        },
      },
      {
        state: 'new',
        makeHtml(state) {
          return html`
            <div data-is-rank-new class="${staticFont('unica_14_bold')}">
              ${CreatorUtilS.diffIcons[state]}
            </div>
          `;
        },
      },
      {
        state: 'up',
        makeHtml(state, diff) {
          return html`
            <div data-is-rank-up class="${staticFont('unica_16_bold')}">
              ${CreatorUtilS.diffIcons[state]}${Math.abs(diff)}
            </div>
          `;
        },
      },
      {
        state: 'down',
        makeHtml(state, diff) {
          return html`
            <div data-is-rank-down class="${staticFont('unica_16_bold')}">
              ${CreatorUtilS.diffIcons[state]}${Math.abs(diff)}
            </div>
          `;
        },
      },
      {
        state: 'zero',
        makeHtml() {
          return html``;
        },
      },
    ];
  }

  get isMobile(): boolean {
    return this.data.is_mobile;
  }
}

class CreatorRankerList extends View<{
  creator_rankings: CreatorTypeS.ICreatorRanking[];
  updated_at: CreatorRankingTopTenProps['updated_at'];
  ranking_page_type: CreatorRankingTopTenProps['ranking_page_type'];
  is_mobile: CreatorRankingTopTenProps['is_mobile'];
}> {
  protected override template() {
    return html` <div class="${klass.creator_ranker_list}">${this._makeCreatorRankerCardViews()}</div>`;
  }

  private _makeCreatorRankerCardViews() {
    const rankers = this._filterRankersByCondition();
    const { updated_at, ranking_page_type, is_mobile } = this.data;
    return rankers.map(
      (creator_ranking) =>
        new CreatorRankerCard({
          creator_ranking,
          updated_at,
          ranking_page_type,
          is_mobile,
        }),
    );
  }

  private _filterRankersByCondition() {
    return pipe(
      this.data.creator_rankings,
      // 모바일의 경우 1위를 제외한 9개만 렌더링
      filter(({ current_rank }) => !this.isMobile || (this.isMobile && current_rank !== 1)),
      toArray,
    );
  }

  get isMobile(): boolean {
    return this.data.is_mobile;
  }
}

export class CreatorRankingTopTen extends View<CreatorRankingTopTenProps> {
  override template() {
    const { top_one, top_ten } = this._makeTopCreatorData();
    const { updated_at, ranking_page_type, is_mobile } = this.data;

    return html`
      <div class="${klass.container}">
        <!--   top 1   -->
        ${new CreatorTopRankerCard({
          creator_ranking: top_one,
          updated_at,
          ranking_page_type,
          is_mobile,
        })}

        <!--   top 10   -->
        ${new CreatorRankerList({
          creator_rankings: top_ten,
          updated_at,
          ranking_page_type,
          is_mobile,
        })}
      </div>
    `;
  }

  private _makeTopCreatorData = (): {
    top_one: CreatorTypeS.ICreatorRanking;
    top_ten: CreatorTypeS.ICreatorRanking[];
  } => {
    const { creator_rankings } = this.data;

    const top_ten = pipe(creator_rankings, take(10), toArray);

    const top_one = head(creator_rankings)!;

    return { top_one, top_ten };
  };

  public setDataAndRedraw(custom_data: Partial<CreatorRankingTopTenProps>) {
    Object.assign(this.data, custom_data);
    this.redraw();
  }
}
